import { useCallback } from "react";
import { type QueryClient, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { isAxiosError } from "axios";
import {
    cancelTaskUpdateQueries,
    invalidateCloseHealthQueries,
    invalidateUpdatedTaskQueries,
    requestTaskUpdate,
    restoreTaskFromUpdateSnapshot,
    type TaskUpdateBody,
    type TaskUpdateSnapshot,
    writeOptimisticTaskUpdate,
} from "@api/task.api";
import type { Task } from "@common/types/task";
import { useUser } from "@hooks/useUser";
import { getIsViewOnlyInWorkspace } from "./taskListViewOnly";

export interface TaskListUpdateRequest {
    task: Task;
    body: TaskUpdateBody;
}

export type TaskListUpdate = (request: TaskListUpdateRequest) => Promise<void>;

/**
 * One task update: optimistic unless view-only, rolled back on failure. The
 * rollback restores only this task, as it was when this update started, so a
 * second in-flight edit on the same task and the blocked-by counts a finishing
 * status moved on other rows stay wrong until the `finally` refetch lands.
 */
const runTaskListUpdate = async ({
    queryClient,
    userId,
    task,
    body,
}: TaskListUpdateRequest & {
    queryClient: QueryClient;
    userId: number | undefined;
}) => {
    const workspaceKey = task.workspaceId;
    const isViewOnly = await getIsViewOnlyInWorkspace({
        queryClient,
        userId,
        workspaceId: task.workspaceId,
    });
    let snapshot: TaskUpdateSnapshot | undefined;
    try {
        if (!isViewOnly) {
            await cancelTaskUpdateQueries({
                queryClient,
                workspaceKey,
                taskId: task.id,
            });
            snapshot = writeOptimisticTaskUpdate({
                queryClient,
                workspaceKey,
                task,
                payload: body,
            });
        }
        await requestTaskUpdate({ task, body });
    } catch (error) {
        if (snapshot) {
            restoreTaskFromUpdateSnapshot({
                queryClient,
                snapshot,
                taskId: task.id,
            });
        }
        if (!isAxiosError(error) || !error.response) {
            Sentry.captureException(error);
        }
    } finally {
        invalidateUpdatedTaskQueries({
            queryClient,
            workspaceKey,
            taskId: task.id,
        });
        invalidateCloseHealthQueries({ queryClient, task, body });
    }
};

/** Task list updates keyed by each task's workspace: optimistic unless view-only, rolled back on failure; the server's toast is the only message. */
export const useTaskListUpdate = (): TaskListUpdate => {
    const queryClient = useQueryClient();
    const userId = useUser()?.id;

    return useCallback(
        ({ task, body }: TaskListUpdateRequest) =>
            runTaskListUpdate({ queryClient, userId, task, body }),
        [queryClient, userId],
    );
};
